/**
 * PriceChartCore — the single lightweight-charts renderer shared by the inline
 * peek (ChartModal) and the full page (ChartPage).
 *
 * WHY THIS EXISTS
 * ───────────────
 * Price charting used to be TWO independent copies of the same
 * `createChart(...) + addSeries(CandlestickSeries) + volume histogram + resize
 * observer` wiring — one in ChartModal, one in ChartPage. They drifted (the
 * modal fetched /ohlcv, the page fetched /technicals; currency handling and
 * colours diverged), which is exactly the class of bug that bites when the same
 * thing is implemented twice. This is the one renderer both now drive.
 *
 * DESIGN — "lightweight then all the sophisticated"
 * ─────────────────────────────────────────────────
 * The core takes already-fetched `bars` plus a set of feature flags. Callers
 * opt in to sophistication rather than the core guessing:
 *   • peek  (modal): candles + volume only            → pass no overlays/markers
 *   • full  (page):  SMA/BB overlays, OB/OS + signal   → pass them in
 *                    markers, SMC / trade price-lines
 *
 * The core intentionally does NOT fetch, own toolbars, or hold ticker state —
 * that stays with the caller (ChartPage keeps its rich toolbar; ChartModal
 * stays a thin shell). This keeps the extraction a pure dedup with no behaviour
 * change to either surface.
 */
import { useEffect, useRef } from 'react'
import {
  createChart,
  createSeriesMarkers,
  CandlestickSeries,
  HistogramSeries,
  LineSeries,
  type IChartApi,
  type ISeriesApi,
  type CandlestickData,
  type HistogramData,
  type LineData,
  ColorType,
  CrosshairMode,
  type Time,
  type SeriesType,
  type SeriesMarker,
} from 'lightweight-charts'
import type { TechnicalBar } from '@/lib/types'

// Up/down colours — single source of truth for both surfaces.
const UP = '#10b981'
const DOWN = '#ef4444'

export function toTimestamp(dateStr: string): Time {
  const d = new Date(dateStr)
  return Math.floor(d.getTime() / 1000) as Time
}

export function currencySymbol(currency: string): string {
  const map: Record<string, string> = {
    GBP: '£', GBp: '£', EUR: '€', JPY: '¥', CHF: 'Fr', CAD: 'C$', AUD: 'A$',
  }
  return map[currency] ?? '$'
}

/** A price line the caller wants drawn on the price scale (SMC level, trade
 *  entry/stop/target, etc). */
export interface PriceLineSpec {
  price: number
  color: string
  title: string
  dashed?: boolean
  width?: number
}

/** SMA/BB-style overlay line. Values align 1:1 with `bars` (null = gap). */
export interface OverlayLineSpec {
  key: string
  color: string
  label: string
  values: (number | null)[]
}

export interface PriceChartCoreProps {
  /** Fully-fetched bars (OHLC + optional indicator columns). */
  bars: TechnicalBar[]
  /** ISO currency of the series, for the axis formatter. */
  currency?: string
  /** 'candle' | 'line'. Peek always passes 'candle'. */
  chartType?: 'candle' | 'line'
  /** Show the volume histogram sub-scale. */
  showVolume?: boolean
  /** Pixel height. Modal passes a fixed height; page passes 400. */
  height?: number
  /** Chart background — modal (#111318) vs page (#0f1117) differ slightly, so
   *  keep it caller-controlled to preserve the exact current look. */
  background?: string
  /** Overlay lines (SMA 20/50/200, Bollinger). Empty for peek. */
  overlays?: OverlayLineSpec[]
  /** Markers on the price series (OB/OS, annotations, signals). Empty for peek. */
  markers?: SeriesMarker<Time>[]
  /** Horizontal price lines (SMC structure, blotter trade levels). Empty for peek. */
  priceLines?: PriceLineSpec[]
  /** Show intraday time on the x-axis (true for sub-daily intervals). */
  timeVisible?: boolean
  /** Crosshair hover callback — page uses it to drive its OHLC readout. */
  onCrosshair?: (bar: TechnicalBar | null) => void
  /** Click callback (page annotation mode). Receives the bar under the click. */
  onBarClick?: (bar: TechnicalBar) => void
}

/**
 * Renders ONLY the chart canvas into a full-size div. The caller controls the
 * wrapping layout (modal frame vs page column). Rebuilds the chart whenever any
 * input changes — same lifecycle both surfaces already had.
 */
export default function PriceChartCore({
  bars,
  currency = 'USD',
  chartType = 'candle',
  showVolume = true,
  height = 400,
  background = '#0f1117',
  overlays = [],
  markers = [],
  priceLines = [],
  timeVisible = false,
  onCrosshair,
  onBarClick,
}: PriceChartCoreProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const chartRef = useRef<IChartApi | null>(null)

  useEffect(() => {
    if (!containerRef.current || bars.length === 0) return

    // Dispose any prior chart before rebuilding.
    if (chartRef.current) {
      chartRef.current.remove()
      chartRef.current = null
    }

    const container = containerRef.current
    const chart = createChart(container, {
      width: container.clientWidth,
      height,
      layout: {
        background: { type: ColorType.Solid, color: background },
        textColor: '#9ca3af',
        fontSize: 11,
      },
      grid: {
        vertLines: { color: '#1e2030' },
        horzLines: { color: '#1e2030' },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: { color: '#4b5563', width: 1, style: 2, labelBackgroundColor: '#374151' },
        horzLine: { color: '#4b5563', width: 1, style: 2, labelBackgroundColor: '#374151' },
      },
      rightPriceScale: {
        borderColor: '#1e2030',
        scaleMargins: { top: 0.05, bottom: 0.15 },
      },
      localization: {
        priceFormatter: (price: number) => `${currencySymbol(currency)}${price.toFixed(2)}`,
      },
      timeScale: { borderColor: '#1e2030', timeVisible },
    })
    chartRef.current = chart

    // Ascending by date so lightweight-charts renders left→right.
    const sorted = [...bars].sort((a, b) => a.date.localeCompare(b.date))

    // ── Price series: candle or line ──
    let priceSeries: ISeriesApi<SeriesType>
    if (chartType === 'candle') {
      priceSeries = chart.addSeries(CandlestickSeries, {
        upColor: UP, downColor: DOWN,
        borderUpColor: UP, borderDownColor: DOWN,
        wickUpColor: UP, wickDownColor: DOWN,
      })
      priceSeries.setData(sorted.map(b => ({
        time: toTimestamp(b.date), open: b.open, high: b.high, low: b.low, close: b.close,
      } as CandlestickData)))
    } else {
      priceSeries = chart.addSeries(LineSeries, {
        color: '#3b82f6', lineWidth: 2, crosshairMarkerVisible: true,
      })
      priceSeries.setData(sorted.map(b => ({
        time: toTimestamp(b.date), value: b.close,
      } as LineData)))
    }

    // ── Markers (OB/OS, annotations, signals) ──
    let seriesMarkers: ReturnType<typeof createSeriesMarkers<Time>> | null = null
    if (markers.length > 0) {
      const sortedMarkers = [...markers].sort((a, b) => (a.time as number) - (b.time as number))
      seriesMarkers = createSeriesMarkers<Time>(priceSeries, sortedMarkers)
    }

    // ── Horizontal price lines (SMC / trade levels) ──
    for (const pl of priceLines) {
      if (pl.price == null || !Number.isFinite(pl.price)) continue
      try {
        priceSeries.createPriceLine({
          price: pl.price,
          color: pl.color,
          lineWidth: pl.width ?? 1,
          lineStyle: pl.dashed ? 2 : 0,
          axisLabelVisible: true,
          title: pl.title,
        } as never)
      } catch { /* noop */ }
    }

    // ── Volume histogram on its own bottom scale ──
    if (showVolume) {
      const volume = chart.addSeries(HistogramSeries, {
        priceFormat: { type: 'volume' },
        priceScaleId: 'volume',
      })
      chart.priceScale('volume').applyOptions({ scaleMargins: { top: 0.85, bottom: 0 } })
      volume.setData(sorted.map(b => ({
        time: toTimestamp(b.date),
        value: b.volume,
        color: b.close >= b.open ? 'rgba(16,185,129,0.3)' : 'rgba(239,68,68,0.3)',
      } as HistogramData)))
    }

    // ── Overlay lines (SMA/BB) ──
    for (const ov of overlays) {
      const series = chart.addSeries(LineSeries, {
        color: ov.color, lineWidth: 1, title: ov.label, priceScaleId: 'right',
      })
      const data: LineData[] = []
      for (let i = 0; i < sorted.length; i++) {
        const v = ov.values[i]
        if (v != null) data.push({ time: toTimestamp(sorted[i].date), value: v })
      }
      series.setData(data)
    }

    // ── Crosshair + click → resolve the bar under the cursor ──
    if (onCrosshair || onBarClick) {
      chart.subscribeCrosshairMove(param => {
        if (!param.time || !param.seriesData || param.seriesData.size === 0) {
          onCrosshair?.(null)
          return
        }
        const ts = param.time as number
        const bar = sorted.find(b => (toTimestamp(b.date) as number) === ts)
        onCrosshair?.(bar ?? null)
      })
      if (onBarClick) {
        chart.subscribeClick(param => {
          if (!param.time) return
          const ts = param.time as number
          const bar = sorted.find(b => (toTimestamp(b.date) as number) === ts)
          if (bar) onBarClick(bar)
        })
      }
    }

    // ── Responsive width/height ──
    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        chart.applyOptions({ width: entry.contentRect.width })
      }
    })
    observer.observe(container)
    chart.timeScale().fitContent()

    return () => {
      observer.disconnect()
      seriesMarkers?.detach()
      chart.remove()
      chartRef.current = null
    }
  }, [bars, currency, chartType, showVolume, height, background, overlays, markers, priceLines, timeVisible, onCrosshair, onBarClick])

  return <div ref={containerRef} className="w-full h-full" />
}
